import { Form, Input, message } from 'antd'
import React, {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { PROPERTY_TYPES } from '../../../common/drmTypes'
import { useFetchPropertyRuian } from '../../utils/useFetchPropertyRuian'
import { AuctionMonitoringLinkedPropertyPreviewBox } from '../AuctionMonitoringLinkedPropertyPreviewBox/AuctionMonitoringLinkedPropertyPreviewBox'
import { Fade } from '../Fade/Fade'

const PROPERTY_TYPES_PATTERN = Object.values(PROPERTY_TYPES).join('|')
const PROPERTY_LINK_REGEX = new RegExp(
  `^https:\\/\\/data2\\.regesta\\.cz\\/nemovitosti\\/(${PROPERTY_TYPES_PATTERN})\\/([a-f0-9-]{36})(\\/?(\\?.*)?)?$`
)
const PROPERTY_LINK_PLACEHOLDER =
  'https://data2.regesta.cz/nemovitosti/budova/5d976313-49e1-40fa-9e9b-37f7c3fbf3ed'
const inputRules = [
  {
    required: true,
    message: 'Zadejte prosím odkaz na nemovitost.',
  },
  {
    pattern: PROPERTY_LINK_REGEX,
    message: 'Odkaz na nemovitost není ve správném formátu.',
  },
]

const parsePropertyLink = (link: string) => {
  const match = link.match(PROPERTY_LINK_REGEX)
  if (match) {
    const [, type, id] = match
    return {
      propertyType: type,
      propertyId: id,
    }
  }
  return null
}

export interface AuctionMonitoringLinkedPropertyFormProps {
  auctionId: string
  onClearForm?: () => void
}

export const AuctionMonitoringLinkedPropertyForm: FunctionComponent<AuctionMonitoringLinkedPropertyFormProps> =
  memo(props => {
    const { auctionId, onClearForm } = props
    const fetchRuian = useFetchPropertyRuian()
    const [form] = Form.useForm()

    const [loading, setLoading] = useState(false)
    const [propertyData, setPropertyData] = useState<{
      id: string
      type: PROPERTY_TYPES
    } | null>(null)

    const loadPropertyData = useCallback(
      async (id: string, type: PROPERTY_TYPES) => {
        setLoading(true)
        try {
          await fetchRuian(id, type)
          setPropertyData({ id, type })
          return true
        } catch (error) {
          message.error('Chyba při načítání dat. Zkuste to prosím znovu.')
          return false
        } finally {
          setLoading(false)
        }
      },
      [fetchRuian]
    )

    const handleLinkChange = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const result = parsePropertyLink(value)

        if (result) {
          loadPropertyData(
            result.propertyId,
            result.propertyType as PROPERTY_TYPES
          )
        } else if (value && !PROPERTY_LINK_REGEX.test(value)) {
          setPropertyData(null)
        }
      },
      [loadPropertyData]
    )

    const handleCancel = useCallback(() => {
      setPropertyData(null)
      form.setFieldsValue({ propertyLink: '' })
      if (onClearForm) {
        onClearForm()
      }
    }, [form, onClearForm])

    useEffect(() => {
      if (form) {
        form.getFieldInstance('propertyLink').focus()
      }
    })

    return (
      <Form form={form}>
        <Form.Item
          name="propertyLink"
          label="Odkaz na nemovitost"
          rules={inputRules}
        >
          <Input
            placeholder={PROPERTY_LINK_PLACEHOLDER}
            allowClear
            disabled={loading}
            onChange={handleLinkChange}
            autoFocus
          />
        </Form.Item>

        <Fade show={!!propertyData?.id && !!propertyData?.type}>
          {propertyData && (
            <AuctionMonitoringLinkedPropertyPreviewBox
              auctionId={auctionId}
              propertyId={propertyData.id}
              propertyType={propertyData.type}
              onCancel={handleCancel}
            />
          )}
        </Fade>
      </Form>
    )
  })
